import type { Dispatch, SetStateAction } from 'react';

import Cookies from 'js-cookie';

import type { CouponDiscount, ExtraData, OrderPrices, Product } from '../Checkout.types';

export type PersistedCheckoutOrder = {
  selectedProducts: Product[];
  orderPrices: OrderPrices;
  paymentMethod?: 'klarna' | 'invoice';
  couponDiscount: CouponDiscount | null;
  extraData: ExtraData;
};

const DRONE_ORDER_KEY = 'drone_order';
const SUBSCRIPTION_ORDER_KEY = 'subscription_order';

export function saveDroneOrder(order: PersistedCheckoutOrder): void {
  Cookies.set(DRONE_ORDER_KEY, JSON.stringify(order));
}

export function getDroneOrder(): PersistedCheckoutOrder | undefined {
  const raw = Cookies.get(DRONE_ORDER_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as PersistedCheckoutOrder;
  } catch {
    return undefined;
  }
}

export function clearDroneOrder(): void {
  Cookies.remove(DRONE_ORDER_KEY);
}

export function saveSubscriptionOrder(order: PersistedCheckoutOrder): void {
  sessionStorage.setItem(SUBSCRIPTION_ORDER_KEY, JSON.stringify(order));
}

export function getSubscriptionOrder(): PersistedCheckoutOrder | undefined {
  const raw = sessionStorage.getItem(SUBSCRIPTION_ORDER_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as PersistedCheckoutOrder;
  } catch {
    return undefined;
  }
}

export function clearSubscriptionOrder(): void {
  sessionStorage.removeItem(SUBSCRIPTION_ORDER_KEY);
}

export function hasPersistedOrderPrices(orderPrices?: OrderPrices): boolean {
  return orderPrices?.propertiesTotalPrice !== undefined || orderPrices?.discountPrice !== undefined;
}

export function restorePersistedOrder(
  order: PersistedCheckoutOrder,
  checkout: {
    setSelectedProducts: (products: Product[]) => void;
    setOrderPrices: (prices: OrderPrices) => void;
    setPaymentMethod: (method: 'klarna' | 'invoice') => void;
    setCouponDiscount: (discount: CouponDiscount | null) => void;
    setExtraData: Dispatch<SetStateAction<ExtraData>>;
  }
): void {
  checkout.setSelectedProducts(order.selectedProducts);
  checkout.setOrderPrices(order.orderPrices);
  if (order.paymentMethod) {
    checkout.setPaymentMethod(order.paymentMethod);
  }
  checkout.setCouponDiscount(order.couponDiscount);
  checkout.setExtraData(order.extraData);
}
